/* eslint-disable react-refresh/only-export-components */
import { Link, NavigateOptions } from "@tanstack/react-router";
import React, {
  ContextType,
  createContext,
  memo,
  useContext,
  useMemo,
} from "react";
import { SortDescriptor } from "react-stately";
import { twMerge } from "tailwind-merge";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";

export interface TableItem {
  id: string | number;
}

export interface TableColumnDefinition<
  Item extends TableItem,
  Id extends keyof Item | ((string | number) & {}) =
    | keyof Item
    | ((string | number) & {}),
> {
  id: Extract<Id, string | number>;
  name: string;
  isCheckbox?: boolean;
  isRowHeader?: boolean;
  isSortable?: boolean;
  defaultSortDirection?: "ascending" | "descending";
  width: number;
  ghostWidth?: number;
  grow?: number;
  className?: string;
  headerClassName?: string;
  rowClassName?: string;
  bodyClassName?: string;
  renderCell?: (item: Item) => React.ReactNode;
  renderGhostCell?: () => React.ReactNode;
  renderGroupHeader?: (groupValue: string | number) => React.ReactNode;
}

type CombineStrict<T, U> = keyof (T | U) extends never ? T & U : never;

export const ghostItem: unique symbol = Symbol();
export const createGhostItems = (count: number) =>
  Array.from({ length: count }, (): typeof ghostItem => ghostItem);

export function createTypedTable<T extends TableItem>() {
  function createColumns<Id extends keyof T | ((string | number) & {})>(
    columns: TableColumnDefinition<T, Id>[],
  ) {
    return columns;
  }

  const TableContext = createContext<{
    columns: TableColumnDefinition<T>[];
    items: (T | typeof ghostItem)[];
    sortDescriptor: SortDescriptor;
    setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
    buildItemLink?: (item: T) => NavigateOptions;
    onClickItem?: (item: T) => void;
    selectedIds?: Set<T["id"]>;
    setHoveredId?: (id: T["id"] | null) => void;
    hoveredId?: T["id"] | null;
    setSelectedIds?: (ids: Set<T["id"]>) => void;
    classname?: string;
  }>({
    columns: [],
    items: [],
    sortDescriptor: { column: "", direction: "ascending" },
    setSortDescriptor: () => {},
    setHoveredId: () => {},
    hoveredId: null,
    classname: "",
  });

  const Table = memo(
    ({
      columns,
      items,
      sortDescriptor,
      setSortDescriptor,
      buildItemLink,
      onClickItem,
      selectedIds,
      setSelectedIds,
      setHoveredId,
      hoveredId,
      classname,
      children,
      ...props
    }: CombineStrict<
      {
        columns: TableColumnDefinition<T>[];
        items: (T | typeof ghostItem)[];
        sortDescriptor: SortDescriptor;
        setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
        buildItemLink?: (item: T) => NavigateOptions;
        onClickItem?: (item: T) => void;
        selectedIds?: Set<T["id"]>;
        setSelectedIds?: (ids: Set<T["id"]>) => void;
        setHoveredId?: (id: T["id"] | null) => void;
        hoveredId?: T["id"] | null;
        classname?: string;
      },
      React.JSX.IntrinsicElements["div"]
    >) => {
      const context = useMemo<ContextType<typeof TableContext>>(
        () => ({
          columns,
          items,
          sortDescriptor,
          setSortDescriptor,
          buildItemLink,
          onClickItem,
          selectedIds,
          setSelectedIds,
          setHoveredId,
          hoveredId,
          classname,
        }),
        [
          columns,
          items,
          sortDescriptor,
          setSortDescriptor,
          buildItemLink,
          onClickItem,
          selectedIds,
          setSelectedIds,
          setHoveredId,
          hoveredId,
          classname,
        ],
      );

      return (
        <TableContext.Provider value={context}>
          <div
            role="table"
            aria-colcount={columns.length}
            aria-rowcount={-1}
            {...props}
            className={twMerge(
              "inline-flex min-w-full flex-col self-start",
              props.className,
            )}
          >
            {children ?? (
              <>
                <TableHeader />
                <TableBody />
              </>
            )}
          </div>
        </TableContext.Provider>
      );
    },
  );

  const TableHeader = memo(
    (
      props: React.JSX.IntrinsicElements["div"] & {
        rowClassName?: string;
      },
    ) => {
      const {
        columns,
        items,
        sortDescriptor,
        setSortDescriptor,
        selectedIds,
        setSelectedIds,
      } = useContext(TableContext);
      return (
        <div {...props} role="rowgroup">
          <div className="min-w-max">
            <TableRow className={props.rowClassName} aria-rowindex={1}>
              {columns.map((column, i) => (
                <TableColumn
                  key={column.id}
                  className={twMerge(column.className, column.headerClassName)}
                  style={{
                    width: column.width,
                    flexGrow: column.grow ?? column.width,
                  }}
                  aria-colindex={i + 1}
                  name={column.name}
                  isSortable={column.isSortable}
                  sortDirection={
                    sortDescriptor.column === column.id
                      ? sortDescriptor.direction
                      : undefined
                  }
                  defaultSortDirection={column.defaultSortDirection}
                  onSort={(sortDirection) =>
                    setSortDescriptor({
                      column: column.id,
                      direction: sortDirection,
                    })
                  }
                  width={column.width}
                >
                  {(() => {
                    if (column.renderGroupHeader) {
                      const children = column.renderGroupHeader(column.name);
                      return children === undefined ||
                        children === null ||
                        children === "" ? (
                        <TableCellFallback />
                      ) : (
                        children
                      );
                    }
                  })()}
                  {(() => {
                    if (column.isCheckbox) {
                      let selectedCount = 0;
                      if (selectedIds) {
                        for (const item of items) {
                          if (item === ghostItem) continue;
                          if (selectedIds.has(item.id)) {
                            selectedCount++;
                          }
                        }
                      }
                      return (
                        <TableCheckbox
                          isIndeterminate={
                            selectedCount > 0 && selectedCount < items.length
                          }
                          isSelected={
                            selectedCount > 0 && selectedCount === items.length
                          }
                          onChange={(isSelected) => {
                            setSelectedIds?.(
                              new Set(
                                isSelected
                                  ? items
                                      .map((item) =>
                                        item === ghostItem ? null : item.id,
                                      )
                                      .filter((item) => item !== null)
                                  : null,
                              ),
                            );
                          }}
                        />
                      );
                    }

                    return column.name;
                  })()}
                </TableColumn>
              ))}
            </TableRow>
          </div>
        </div>
      );
    },
  );

  const TableBody = memo((props: React.JSX.IntrinsicElements["div"]) => {
    const {
      columns,
      items,
      buildItemLink,
      onClickItem,
      selectedIds,
      setSelectedIds,
      setHoveredId,
      classname,
    } = useContext(TableContext);

    const getRowWrapperClassName = (item: T | typeof ghostItem) =>
      twMerge(
        String.raw`group/table-row min-w-full block outline-0 outline-blue-500 outline-offset-0 focus-visible:outline-2 transition-colors odd:bg-theme-on-background/3`,
        item !== ghostItem &&
          (buildItemLink || onClickItem) &&
          String.raw`cursor-pointer not-[:has(.group\/table-checkbox:hover)]:hover:bg-theme-on-background/8`,
        classname,
      );

    return (
      <div {...props} role="rowgroup">
        <div className="min-w-max">
          {items.map((item, i) =>
            item === ghostItem ? (
              <div key={`ghost-${i}`} className={getRowWrapperClassName(item)}>
                <TableRow aria-rowindex={i + 2 + items.length}>
                  {columns.map((column, i) => (
                    <TableCell
                      key={column.id}
                      className={twMerge(
                        column.className,
                        column.bodyClassName,
                      )}
                      style={{
                        width: column.width,
                        flexGrow: column.grow ?? column.width,
                      }}
                      aria-colindex={i + 1}
                    >
                      {(() => {
                        if (column.renderGhostCell) {
                          return column.renderGhostCell();
                        }

                        if (column.isCheckbox) {
                          return null;
                        }

                        return (
                          <div className="flex flex-col justify-center">
                            <div
                              className="bg-theme-on-background/16 h-3 w-full max-w-36 animate-pulse rounded-md"
                              style={{
                                width: column.ghostWidth ?? column.width,
                              }}
                            />
                          </div>
                        );
                      })()}
                    </TableCell>
                  ))}
                </TableRow>
              </div>
            ) : (
              <Wrap
                key={`item-${i}`}
                renderWrapper={(children) => {
                  const wrapperProps = {
                    className: getRowWrapperClassName(item),
                    onClick: onClickItem && (() => onClickItem?.(item)),
                    onMouseEnter: () => setHoveredId?.(item.id),
                    onMouseLeave: () => setHoveredId?.(null),
                  };
                  return buildItemLink ? (
                    <Link {...wrapperProps} {...buildItemLink?.(item)}>
                      {children}
                    </Link>
                  ) : onClickItem ? (
                    <button {...wrapperProps}>{children}</button>
                  ) : (
                    <div {...wrapperProps}>{children}</div>
                  );
                }}
                renderChildren={() => (
                  <TableRow
                    aria-rowindex={i + 2}
                    data-lso-table-row-index={i + 1}
                  >
                    {columns.map((column, i) => (
                      <TableCell
                        key={column.id}
                        className={twMerge(
                          column.className,
                          column.bodyClassName,
                          column.rowClassName,
                        )}
                        style={{
                          width: column.width,
                          flexGrow: column.grow ?? column.width,
                        }}
                        aria-colindex={i + 1}
                      >
                        {(() => {
                          if (column.renderCell) {
                            const children = column.renderCell(item);
                            return children === undefined ||
                              children === null ||
                              children === "" ? (
                              <TableCellFallback />
                            ) : (
                              children
                            );
                          }

                          if (column.isCheckbox) {
                            return (
                              <TableCheckbox
                                isSelected={selectedIds?.has(item.id)}
                                onChange={(isSelected) => {
                                  if (!setSelectedIds) return;
                                  const newSelectedIds = new Set(selectedIds);
                                  if (isSelected) {
                                    newSelectedIds.add(item.id);
                                  } else {
                                    newSelectedIds.delete(item.id);
                                  }
                                  setSelectedIds(newSelectedIds);
                                }}
                              />
                            );
                          }

                          const children =
                            column.id in item
                              ? item[column.id as keyof typeof item]
                              : null;

                          return children === undefined ||
                            children === null ||
                            children === "" ? (
                            <TableCellFallback />
                          ) : (
                            <div className="w-full min-w-0 truncate">
                              {String(children)}
                            </div>
                          );
                        })()}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              />
            ),
          )}
        </div>
      </div>
    );
  });

  const TableRow = memo(
    ({ className, children, ...props }: React.JSX.IntrinsicElements["div"]) => {
      return (
        <div
          {...props}
          className={twMerge(
            // focusRing(renderProps),
            "flex gap-x-6 px-8 xl:px-14",
            className,
          )}
          role="row"
        >
          {children}
        </div>
      );
    },
  );

  const TableColumn = memo(
    ({
      className,
      children,
      name,
      isSortable,
      sortDirection,
      defaultSortDirection,
      onSort,
      width,
      ...props
    }: React.JSX.IntrinsicElements["div"] & {
      name: string;
      isSortable?: boolean;
      sortDirection?: "ascending" | "descending" | undefined;
      defaultSortDirection?: "ascending" | "descending";
      onSort?: (sortDirection: "ascending" | "descending") => void;
      width: number;
    }) => {
      return (
        <div
          {...props}
          className={twMerge(
            // focusRing(renderProps),
            "text-theme-on-background-variant h-10 min-w-0 shrink-0 grow text-start text-[10px]/[16px] font-medium",
            className,
          )}
          role="columnheader"
        >
          <div
            className="inline-flex h-full w-full flex-col justify-center text-nowrap"
            style={{ width }}
          >
            {isSortable ? (
              <Button
                className="relative -mx-3 h-8 min-w-0 rounded-md p-0 px-3 text-start font-[inherit] text-[length:inherit]/[inherit] text-inherit! *:w-full"
                intent="tertiary"
                size="sm"
                aria-label={`Sorter efter "${name}"`}
                onPress={() =>
                  onSort?.(
                    sortDirection
                      ? sortDirection === "ascending"
                        ? "descending"
                        : "ascending"
                      : (defaultSortDirection ?? "ascending"),
                  )
                }
              >
                <div className="flex w-full items-center justify-between gap-x-2">
                  <div className="flex flex-col justify-center">{children}</div>

                  <SortDirectionIcon
                    className="-mr-1"
                    sortDirection={sortDirection}
                  />
                </div>
              </Button>
            ) : (
              children
            )}
          </div>
        </div>
      );
    },
  );

  const TableCell = memo(
    ({
      className,
      children,
      isRowHeader,
      ...props
    }: React.JSX.IntrinsicElements["div"] & {
      isRowHeader?: boolean;
    }) => {
      return (
        <div
          {...props}
          className={twMerge(
            // focusRing(renderProps),
            "text-theme-on-background h-9 min-w-0 shrink-0 grow text-start text-[11px]/[16px] font-bold",
            className,
          )}
          role={isRowHeader ? "rowheader" : "gridcell"}
        >
          <div className="flex h-full w-full min-w-0 flex-col justify-center text-nowrap">
            {children}
          </div>
        </div>
      );
    },
  );

  const TableCellFallback = memo(() => {
    return <span className="text-theme-outline">&mdash;</span>;
  });

  const TableCheckbox = memo(
    (props: {
      isSelected?: boolean;
      isIndeterminate?: boolean;
      onChange?: (isSelected: boolean) => void;
    }) => {
      return (
        <Checkbox
          size="xs"
          className="group/table-checkbox -m-1 size-7 justify-center"
          isSelected={props.isSelected}
          isIndeterminate={props.isIndeterminate}
          onChange={props.onChange}
        />
      );
    },
  );

  return {
    createColumns,
    TableContext,
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableColumn,
    TableCell,
    TableCellFallback,
  };
}

export const SortDirectionIcon = memo(
  ({
    className,
    sortDirection,
    ...props
  }: React.JSX.IntrinsicElements["svg"] & {
    sortDirection?: "ascending" | "descending" | undefined;
  }) => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
        className={twMerge(
          "lucide lucide-chevrons-up-down size-4.5 shrink-0",
          className,
        )}
      >
        <path
          className={
            sortDirection === "ascending"
              ? "stroke-theme-on-background"
              : "stroke-theme-on-background/32"
          }
          d="m7 9 5-5 5 5"
        />

        <path
          className={
            sortDirection === "descending"
              ? "stroke-theme-on-background"
              : "stroke-theme-on-background/32"
          }
          d="m7 15 5 5 5-5"
        />
      </svg>
    );
  },
);

function Wrap({
  renderWrapper,
  renderChildren,
}: {
  renderWrapper: (children: React.ReactNode) => React.ReactNode;
  renderChildren: () => React.ReactNode;
}) {
  return renderWrapper(renderChildren());
}

export const {
  createColumns,
  TableContext,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableColumn,
  TableCell,
  TableCellFallback,
} = createTypedTable();
